import React, { useState, useEffect } from 'react'
import { Modal } from '@workwhile/ui'
import Loading from '../Shared/loading'
import styled from 'styled-components'
import { getCallList, sendMessage } from '../../actions/messaging_actions'
import { showErrorToast } from '../utils/util'

interface Props {
  showModal: boolean
  hideModal: () => void
  submitMessageCallback: () => void
  shiftId?: number
}

const ComposeMessageModal = ({
  showModal,
  hideModal,
  submitMessageCallback,
  shiftId,
}: Props) => {
  const [messageToSend, setMessageToSend] = useState(null)
  const [composeShiftId, setComposeShiftId] = useState(shiftId || null)
  const [callList, setCallList] = useState(null)
  const [loadingCallList, setLoadingCallList] = useState(null)
  const [userIds, setUserIds] = useState(null)
  const [workwhileExperienced, setNewWorker] = useState(false)
  const [companyExperience, setCompanyExperience] = useState(false)
  const [orientationApproved, setOrientationApproved] = useState(null)
  const [listingNotViewed, setListingNotViewed] = useState(false)
  const [distance, setDistance] = useState(null)
  const [backgroundCheckCleared, setBackgroundCheckCleared] = useState(false)
  const [excludeMessaged, setExcludeMessaged] = useState(false)
  const [onlyListingViewed, setOnlyListingViewed] = useState(false)
  const [onWaitlist, setOnWaitlist] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onLoadCallList = (event) => {
    if (!composeShiftId) {
      alert('need a shift id')
      return
    }

    // get call list
    setLoadingCallList(true as any)
    getCallList(composeShiftId)
      .then((res) => {
        setCallList(res.data)
        setLoadingCallList(false as any)
      })
      .catch((err) => {
        setLoadingCallList(false as any)
        showErrorToast(err.message)
      })

    return
  }

  useEffect(() => {
    if (callList) {
      setUserIds(filterCallList())
    }
  }, [
    callList,
    distance,
    orientationApproved,
    companyExperience,
    workwhileExperienced,
    listingNotViewed,
    backgroundCheckCleared,
    excludeMessaged,
    onlyListingViewed,
    onWaitlist,
  ])

  const filterCallList = () => {
    return (callList as any)
      .filter((item) => {
        if (companyExperience && item.shifts_at_company == 0) return false

        if (workwhileExperienced && item.tier_experienced == false) return false

        if (listingNotViewed && item.listing_viewed == 'Y') return false

        if (excludeMessaged && item.messaged == 'Y') return false

        if (backgroundCheckCleared && !item.background_check_status)
          return false

        if (onlyListingViewed && item.listing_viewed == 'N') return false

        if (onWaitlist && item.on_waitlist == 'N') return false

        if (orientationApproved && item.orientation_status != 'approved')
          return false

        // if they're farter away than what we want, pass
        if (distance && item.distance > distance) return false
        else return true
      })
      .map((item) => item.id)
  }

  const onSubmit = (event) => {
    event.preventDefault()
    // console.log("we in here?????? ")
    if (!messageToSend) {
      showErrorToast('Add a message')
      return
    }

    setIsSubmitting(true)

    const userIdsArray =
      typeof userIds == 'string' ? (userIds as any).split(',') : userIds
    sendMessage(userIdsArray, messageToSend, composeShiftId)
      .then(() => {
        if (submitMessageCallback) {
          submitMessageCallback()
        }
        setIsSubmitting(false)
      })
      .catch((err) => {
        showErrorToast(err.message)
        setIsSubmitting(false)
      })
  }

  return (
    <Modal
      open={showModal}
      onClose={hideModal}
      title="Send message for a shift"
    >
      <div style={{ overflow: 'scroll' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ margin: '5px 0 0 0' }}>
            <label>Shift ID&nbsp;</label>
            <input
              className="input"
              name="shiftId"
              value={composeShiftId as any}
              onChange={(e) => setComposeShiftId(e.target.value as any)}
              type="text"
              placeholder="Shift Id"
            />
            <button
              type="submit"
              className="ml-2"
              style={{ width: '150px', margin: '0 0 10px 0' }}
              onClick={onLoadCallList}
              disabled={loadingCallList as any}
            >
              Load call list
            </button>
          </div>
          <label>
            <input
              type="checkbox"
              checked={workwhileExperienced}
              onChange={() => setNewWorker(!workwhileExperienced)}
            />
            &nbsp;
            {'>='} 1 non-orientation shift&nbsp;&nbsp;
            <input
              type="checkbox"
              checked={companyExperience}
              onChange={() => setCompanyExperience(!companyExperience)}
            />
            &nbsp; Company experience&nbsp;&nbsp;
          </label>
          <label>
            <input
              type="checkbox"
              checked={listingNotViewed}
              onChange={() => setListingNotViewed(!listingNotViewed)}
            />
            &nbsp; Listing not viewed&nbsp;&nbsp;
            <input
              type="checkbox"
              checked={backgroundCheckCleared}
              onChange={() =>
                setBackgroundCheckCleared(!backgroundCheckCleared)
              }
            />
            &nbsp; Background check cleared&nbsp;&nbsp;
            <input
              type="checkbox"
              checked={excludeMessaged}
              onChange={() => setExcludeMessaged(!excludeMessaged)}
            />
            &nbsp; Exclude messaged&nbsp;&nbsp;
          </label>
          <label>
            <input
              type="checkbox"
              checked={orientationApproved as any}
              onChange={() =>
                setOrientationApproved(!orientationApproved as any)
              }
            />
            &nbsp; Orientation approved&nbsp;&nbsp;
            <input
              type="checkbox"
              checked={onlyListingViewed}
              onChange={() => setOnlyListingViewed(!onlyListingViewed)}
            />
            &nbsp; Listing Viewed&nbsp;&nbsp;
            <input
              type="checkbox"
              checked={onWaitlist}
              onChange={() => setOnWaitlist(!onWaitlist)}
            />
            &nbsp; On Waitlist&nbsp;&nbsp;
          </label>
          <label>
            Distance&nbsp;
            <input
              type="number"
              step="0.01"
              value={distance as any}
              onChange={(e) => setDistance(e.target.value as any)}
            />
          </label>
          <label>User IDs</label>
          <input
            className="input"
            name="userIds"
            value={userIds as any}
            onChange={(e) => setUserIds(e.target.value as any)}
            type="text"
            placeholder="User Ids Comma Separated"
          />
        </div>
        {loadingCallList ? (
          <div>
            <Loading />
            <p>Loading call list. Hang on it takes a few mins.</p>
          </div>
        ) : null}
        {callList ? (
          <CallListText>
            {userIds ? (userIds as any).length : 0} workers on the current Call
            List
          </CallListText>
        ) : null}
        <label>Message</label>
        <textarea
          rows={5}
          style={{ maxWidth: 500, width: '100%' }}
          name="messageToSend"
          value={messageToSend as any}
          onChange={(e) => setMessageToSend(e.target.value as any)}
          placeholder="Use $first_name to add name as variable"
        />
        <button
          type="submit"
          className="ml-2"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send message'}
        </button>
      </div>
    </Modal>
  )
}

const CallListText = styled.p`
  margin-top: 20px;
  margin-bottom: 20px;
  font-size: 20px;
`

export default ComposeMessageModal
